import type {
  StockBalance,
  StockCommand,
  StockCommandResult,
  StockItem,
  StockItemInput,
  StockMovementPage,
  WarehouseLocation,
  WarehouseLocationInput,
} from '../domain/stock';
import {
  stockBalanceSchema,
  stockItemSchema,
  stockMovementSchema,
  warehouseLocationSchema,
} from '../domain/validation';
import { blobToBase64 } from '../lib/stock-photo';
import { ApiError } from './errors';
import { fulfillmentErrorFrom } from './live-fulfillment-api';
import type { StockApi, StockBalanceFilters, StockMovementFilters } from './stock-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

const commandPaths: Record<StockCommand['kind'], string> = {
  receive: 'receive',
  transfer: 'transfer',
  return: 'return',
  adjust: 'adjust',
  openingBalance: 'openingBalance',
};

/** int64 quantities travel as strings. */
function wireCommand(command: StockCommand): Record<string, unknown> {
  const base = {
    stockItemId: command.stockItemId,
    note: command.note?.trim() || undefined,
    idempotencyKey: command.idempotencyKey,
  };
  switch (command.kind) {
    case 'transfer':
      return {
        ...base,
        fromLocationId: command.fromLocationId,
        toLocationId: command.toLocationId,
        quantity: String(command.quantity),
      };
    case 'adjust':
      return {
        ...base,
        locationId: command.locationId,
        quantityDelta: String(command.quantityDelta),
      };
    default:
      return {
        ...base,
        locationId: command.locationId,
        quantity: String(command.quantity),
        referenceType: command.referenceType?.trim() || undefined,
        referenceId: command.referenceId?.trim() || undefined,
      };
  }
}

// '' usage is sent as unspecified, which clears it; int64 quantities go as strings.
function stockItemBody(input: StockItemInput): Record<string, unknown> {
  const { usage, minimumQuantity, ...rest } = input;
  return {
    ...rest,
    usage: usage === undefined ? undefined : usage || 'STOCK_USAGE_UNSPECIFIED',
    minimumQuantity: minimumQuantity === undefined ? undefined : String(minimumQuantity),
  };
}

/** Talks to leatherback's StockService through the local relay (see server/). */
export class LiveStockApi implements StockApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      throw fulfillmentErrorFrom(response.status, body);
    }
    return body;
  }

  private async listAll(path: string, params: URLSearchParams, field: string): Promise<unknown[]> {
    const rows: unknown[] = [];
    params.set('pageSize', '1000');
    for (;;) {
      const raw = await this.fetchJSON(`${path}?${params.toString()}`);
      const page = raw[field];
      if (Array.isArray(page)) rows.push(...(page as unknown[]));
      const next = raw.nextPageToken;
      if (typeof next !== 'string' || !next) return rows;
      params.set('pageToken', next);
    }
  }

  async listStockItems(includeInactive = false): Promise<StockItem[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    const rows = await this.listAll('/v1/stockItems', params, 'stockItems');
    return rows.map((row) => stockItemSchema.parse(row));
  }

  async createStockItem(input: StockItemInput): Promise<StockItem> {
    const raw = await this.fetchJSON('/v1/stockItems', {
      method: 'POST',
      body: JSON.stringify(stockItemBody(input)),
    });
    return stockItemSchema.parse(raw);
  }

  async updateStockItem(id: string, version: number, input: StockItemInput): Promise<StockItem> {
    // The body is the StockItem itself, so replace_barcodes rides in the
    // query: given barcodes replace the item's (an empty list clears them).
    const query = input.barcodes !== undefined ? '?replaceBarcodes=true' : '';
    const raw = await this.fetchJSON(`/v1/stockItems/${encodeURIComponent(id)}${query}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...stockItemBody(input), version: String(version) }),
    });
    return stockItemSchema.parse(raw);
  }

  async setStockItemImage(id: string, image: Blob): Promise<StockItem> {
    const raw = await this.fetchJSON(`/v1/stockItems/${encodeURIComponent(id)}/image`, {
      method: 'POST',
      body: JSON.stringify({ image: await blobToBase64(image), contentType: image.type }),
    });
    return stockItemSchema.parse(raw);
  }

  async deleteStockItemImage(id: string): Promise<StockItem> {
    const raw = await this.fetchJSON(`/v1/stockItems/${encodeURIComponent(id)}/image`, {
      method: 'DELETE',
    });
    return stockItemSchema.parse(raw);
  }

  stockItemImageUrl(item: StockItem): string | undefined {
    // The image id changes with each photo, so a cached older one isn't shown.
    return item.imageId
      ? `${BASE_URL}/v1/stockItems/${encodeURIComponent(item.id)}/image?v=${encodeURIComponent(item.imageId)}`
      : undefined;
  }

  async listWarehouseLocations(
    siteId?: string,
    includeInactive = false,
  ): Promise<WarehouseLocation[]> {
    const params = new URLSearchParams();
    if (siteId) params.set('siteId', siteId);
    if (includeInactive) params.set('includeInactive', 'true');
    const rows = await this.listAll('/v1/warehouseLocations', params, 'warehouseLocations');
    return rows.map((row) => warehouseLocationSchema.parse(row));
  }

  async createWarehouseLocation(input: WarehouseLocationInput): Promise<WarehouseLocation> {
    const raw = await this.fetchJSON('/v1/warehouseLocations', {
      method: 'POST',
      body: JSON.stringify({ ...input, parentId: input.parentId || undefined }),
    });
    return warehouseLocationSchema.parse(raw);
  }

  async updateWarehouseLocation(
    id: string,
    version: number,
    input: Pick<WarehouseLocationInput, 'code' | 'displayName' | 'type' | 'active'>,
  ): Promise<WarehouseLocation> {
    const raw = await this.fetchJSON(`/v1/warehouseLocations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...input, version: String(version) }),
    });
    return warehouseLocationSchema.parse(raw);
  }

  async listStockBalances(filters?: StockBalanceFilters): Promise<StockBalance[]> {
    const params = new URLSearchParams();
    if (filters?.stockItemId) params.set('stockItemId', filters.stockItemId);
    if (filters?.locationId) params.set('locationId', filters.locationId);
    if (filters?.siteId) params.set('siteId', filters.siteId);
    const raw = await this.fetchJSON(`/v1/stockBalances?${params.toString()}`);
    const rows = Array.isArray(raw.stockBalances) ? raw.stockBalances : [];
    return rows.map((row) => stockBalanceSchema.parse(row));
  }

  async listStockMovements(
    filters?: StockMovementFilters,
    pageToken?: string,
  ): Promise<StockMovementPage> {
    const params = new URLSearchParams({ pageSize: '50' });
    if (filters?.stockItemId) params.set('stockItemId', filters.stockItemId);
    if (filters?.locationId) params.set('locationId', filters.locationId);
    if (pageToken) params.set('pageToken', pageToken);
    const raw = await this.fetchJSON(`/v1/stockMovements?${params.toString()}`);
    const rows = Array.isArray(raw.stockMovements) ? raw.stockMovements : [];
    return {
      movements: rows.map((row) => stockMovementSchema.parse(row)),
      nextPageToken:
        typeof raw.nextPageToken === 'string' && raw.nextPageToken ? raw.nextPageToken : undefined,
    };
  }

  async recordStockCommand(command: StockCommand): Promise<StockCommandResult> {
    const raw = await this.fetchJSON(`/v1/stockMovements:${commandPaths[command.kind]}`, {
      method: 'POST',
      body: JSON.stringify(wireCommand(command)),
    });
    const balances = Array.isArray(raw.balances) ? raw.balances : [];
    return {
      movement: stockMovementSchema.parse(raw.movement),
      replayed: raw.replayed === true,
      balances: balances.map((row) => stockBalanceSchema.parse(row)),
    };
  }
}

export const stockApi = new LiveStockApi();
